import { wrapGlobal } from '../runtime/legacy.js';

/**
 * `#/privacy` and `#/terms` routes.
 *
 * The canonical documents are `public/privacy.html` and `public/terms.html` —
 * standalone pages, because the store listings need a plain public URL and
 * because they should be indexable. These routes render the same files inside
 * the app rather than keeping a second copy of the text that would drift out
 * of sync with them.
 *
 * In-app rendering is not optional: both stores expect the policy to be
 * reachable from within the app, and in a native shell a relative `.html` link
 * with `target="_blank"` opens nothing at all.
 *
 * Deliberately reachable while signed out — reviewers check the policy before
 * creating an account.
 */

const DOCS = {
  '/privacy': { file: 'privacy.html', title: 'Privacy Policy' },
  '/terms': { file: 'terms.html', title: 'Terms of Service' },
};

/** Cache per document — these never change within a session. */
const cache = new Map();

/**
 * Pulls the document body out of the standalone page.
 *
 * Both files wrap their content in `.wrap`; parsing rather than string-slicing
 * means a change to their markup degrades to "render everything in body"
 * instead of rendering broken HTML. Parsed via DOMParser, so the page's own
 * <style> and <script> tags are inert and never applied to the app shell.
 */
function extractBody(html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const root = parsed.querySelector('.wrap') || parsed.body;

  root.querySelectorAll('script, style').forEach((el) => el.remove());
  // The standalone pages carry their own <h1>; the app renders its own title.
  root.querySelector('h1')?.remove();

  return root.innerHTML;
}

async function loadDoc(file) {
  if (cache.has(file)) return cache.get(file);

  // Relative to the document, so this resolves under Vite's `base: './'` on
  // the web and under the capacitor:// origin in the native shells alike.
  const response = await fetch(new URL(file, document.baseURI));
  if (!response.ok) throw new Error(`${response.status} loading ${file}`);

  const body = extractBody(await response.text());
  cache.set(file, body);
  return body;
}

function shell(title, inner) {
  return `
    <div class="page-title">${title}</div>
    <div class="legal-doc card">${inner}</div>
    <div style="margin-top:20px;">
      <a href="#/support" class="btn btn-secondary">Back to Help</a>
    </div>
  `;
}

async function renderLegal({ file, title }) {
  const container = document.getElementById('view-container');
  if (!container) return;

  // Charts hold canvases that leak if the view is replaced without disposing
  // them; the legacy router does the same teardown on every route change.
  const charts = globalThis.AppState?.chartInstances || {};
  for (const key of Object.keys(charts)) {
    charts[key]?.destroy?.();
    delete charts[key];
  }

  container.className = 'view-enter';
  container.innerHTML = shell(title, '<p>Loading…</p>');
  window.scrollTo(0, 0);

  try {
    container.innerHTML = shell(title, await loadDoc(file));
  } catch (err) {
    console.error('[ironpact] could not load legal document', err);
    container.innerHTML = shell(
      title,
      `<p>This document could not be loaded. You can read it at
       <a href="./${file}" target="_blank" rel="noopener">/${file}</a>.</p>`,
    );
  }
}

export function installLegalRoutes() {
  wrapGlobal('handleRoute', function (next) {
    const route = (window.location.hash || '#/').slice(1) || '/';
    const doc = DOCS[route];
    if (!doc) return next();
    return renderLegal(doc);
  });
}
